import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Shield, Mail, Trash2, ShieldOff, CheckCircle, XCircle, Building } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const teamTh = { padding: '12px 14px', textAlign: 'left', color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const teamTd = { padding: '12px 14px', color: 'var(--text-secondary)', fontSize: 12, whiteSpace: 'nowrap' };

export default function TeamPage() {
  const [teamOrg, setTeamOrg] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('sdr');
  const [teamLoading, setTeamLoading] = useState(false);
  const [performance, setPerformance] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditAction, setAuditAction] = useState('');
  const [auditAdmin, setAuditAdmin] = useState('');
  const [perfDays, setPerfDays] = useState(30);
  const [bulkLeadNames, setBulkLeadNames] = useState('');
  const [bulkSdrId, setBulkSdrId] = useState('');
  const [notifEmail, setNotifEmail] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [seats, setSeats] = useState({ used: 0, pending: 0, max: 10, available: 10 });
  const [orgTimeZone, setOrgTimeZone] = useState('UTC');
  const [orgLanguage, setOrgLanguage] = useState('en');
  const [orgLogoUrl, setOrgLogoUrl] = useState('');
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [memberToSuspend, setMemberToSuspend] = useState(null);
  const [memberToCancelInvite, setMemberToCancelInvite] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const loadTeam = async (pageOverride, actionOverride, adminOverride) => {
    try {
      const res = await api.get('/api/organization');
      setTeamOrg(res.data.organization);
      setTeamMembers(res.data.members || []);
      setSeats(res.data.seats || seats);
      setOrgTimeZone(res.data.organization?.timezone || 'UTC');
      setOrgLanguage(res.data.organization?.language || 'en');
      setOrgLogoUrl(res.data.organization?.logo_url || '');
      setNotifEmail(res.data.organization?.notification_email || '');
      if (res.data.organization) {
        const perf = await api.get(`/api/organization/team-performance?days=${perfDays}`);
        setPerformance(perf.data.members || []);
        const logs = await api.get('/api/organization/audit-logs', {
          params: { page: pageOverride || auditPage, limit: 20, action: actionOverride || auditAction || undefined, admin_email: adminOverride || auditAdmin || undefined }
        });
        setAuditLogs(logs.data.logs || []);
        setAuditTotal(logs.data.total || 0);
        setAuditLogs(logs.data.logs || []);
      }
    } catch (e) {
      console.error('Team load error', e);
    }
  };

  useEffect(() => { loadTeam(); }, []);

  const handleCreateOrg = async () => {
    try {
      const res = await api.post('/api/organization', { name: 'My Organization', timezone: 'UTC', language: 'en' });
      setTeamOrg(res.data);
      toast.success('Organization created');
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleUpdateOrgName = async () => {
    if (!teamOrg?.name?.trim()) return;
    try {
      const res = await api.patch('/api/organization', { name: teamOrg.name, timezone: orgTimeZone, language: orgLanguage });
      setTeamOrg(res.data);
      toast.success('Organization name updated');
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setTeamLoading(true);
    try {
      await api.post('/api/organization/invite', { email: inviteEmail.trim(), role: inviteRole });
      toast.success('Invitation sent');
      setInviteEmail('');
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invite failed');
    } finally { setTeamLoading(false); }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await api.patch(`/api/organization/members/${memberId}`, { role: newRole });
      toast.success('Role updated');
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const confirmRemoveMember = async () => {
    if (!memberToRemove) return;
    const memberId = memberToRemove.id;
    try {
      await api.delete(`/api/organization/members/${memberId}`);
      toast.success('Member removed');
      setMemberToRemove(null);
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove member');
    }
  };

  const confirmCancelInvite = async () => {
    if (!memberToCancelInvite) return;
    const inviteId = -memberToCancelInvite.id;
    try {
      await api.delete(`/api/organization/invites/${inviteId}`);
      toast.success('Invitation cancelled');
      setMemberToCancelInvite(null);
      loadTeam();
    } catch (e) {
      toast.error('Failed to cancel invitation');
    }
  };

  const confirmSuspendMember = async () => {
    if (!memberToSuspend) return;
    const memberId = memberToSuspend.id;
    const newStatus = memberToSuspend.team_status === 'suspended' ? 'active' : 'suspended';
    try {
      await api.patch(`/api/organization/members/${memberId}/status`, { role: newStatus });
      toast.success(newStatus === 'suspended' ? 'Member suspended' : 'Member activated');
      setMemberToSuspend(null);
      loadTeam();
    } catch (e) {
      toast.error('Failed to update member status');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await api.delete(`/api/organization/members/${memberId}`);
      toast.success('Member removed');
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove member');
    }
  };

  return (
    <div className="page-content" style={{ maxWidth: 1000, margin: '0 auto' }}>
      <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Users size={22} color="var(--accent-primary)" /> Team & Organization
      </h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 24px' }}>
        Manage your team, invite SDRs, and track performance.
      </p>

      {teamOrg && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 14, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Team Seats</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{seats.used + seats.pending} / {seats.max} used</span>
          </div>
          <div style={{ height: 8, background: 'var(--bg-tertiary)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, ((seats.used + seats.pending) / seats.max) * 100)}%`, height: '100%', background: 'var(--accent-gradient)', borderRadius: 4 }} />
          </div>
          <p style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 6 }}>
            {seats.available} seats available. Pending invites: {seats.pending}
          </p>
        </div>
      )}

      {teamOrg ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <input
              value={teamOrg.name || ''}
              onChange={e => setTeamOrg(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Organization name"
              style={{ flex: 1, minWidth: 240, padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
            />
            <button onClick={handleUpdateOrgName} style={{ padding: '12px 22px', borderRadius: 8, border: 'none', background: 'var(--accent-gradient)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Update Organization
            </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Organization Timezone</label>
              <select value={orgTimeZone} onChange={e => setOrgTimeZone(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                <option value="UTC">UTC</option>
                <option value="Africa/Lagos">West Africa Time (GMT+1)</option>
                <option value="Africa/Nairobi">East Africa Time (GMT+3)</option>
                <option value="Europe/London">London (GMT+0)</option>
                <option value="Europe/Paris">Paris / Berlin (GMT+1)</option>
                <option value="America/New_York">Eastern Time (GMT-5)</option>
                <option value="America/Chicago">Central Time (GMT-6)</option>
                <option value="America/Denver">Mountain Time (GMT-7)</option>
                <option value="America/Los_Angeles">Pacific Time (GMT-8)</option>
                <option value="Asia/Dubai">Dubai (GMT+4)</option>
                <option value="Asia/Kolkata">India (GMT+5:30)</option>
                <option value="Asia/Singapore">Singapore (GMT+8)</option>
                <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
                <option value="Australia/Sydney">Sydney (GMT+10)</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={{ display: 'block', fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Organization Logo</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                {logoPreview || orgLogoUrl ? (
                  <img src={logoPreview || orgLogoUrl} alt="Logo" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'contain', background: 'var(--bg-tertiary)' }} />
                ) : (
                  <div style={{ width: 56, height: 56, borderRadius: 10, background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}>
                    <Building size={24} />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <input id="logo-file-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      // Client-side resize using canvas
                      const img = new Image();
                      const url = URL.createObjectURL(file);
                      img.onload = async () => {
                        const canvas = document.createElement('canvas');
                        const max = 400;
                        let { width, height } = img;
                        if (width > height && width > max) { height = height * max / width; width = max; }
                        else if (height > width && height > max) { width = width * max / height; height = max; }
                        else if (width > max) { width = max; height = height * max / img.width; }
                        canvas.width = width; canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, width, height);
                        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
                        URL.revokeObjectURL(url);
                        const formData = new FormData();
                        formData.append('file', blob, file.name || 'logo.jpg');
                        const res = await api.post('/api/organization/logo-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setOrgLogoUrl(res.data.logo_url);
                        setLogoPreview(res.data.logo_url);
                        toast.success('Logo uploaded');
                        loadTeam();
                      };
                      img.src = url;
                    } catch { toast.error('Failed to process image'); }
                  }} />
                  <button onClick={() => document.getElementById('logo-file-input').click()} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    Upload Logo
                  </button>
                </div>
              </div>
              <input value={orgLogoUrl} onChange={e => setOrgLogoUrl(e.target.value)} placeholder="or paste logo URL" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', fontFamily: 'inherit' }} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Notification Email</label>
              <input value={notifEmail} onChange={e => setNotifEmail(e.target.value)} placeholder="admin@company.com" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
              <button onClick={async () => {
                try {
                  await api.patch('/api/organization/notification-email', { notification_email: notifEmail });
                  toast.success('Notification email updated');
                } catch { toast.error('Failed to update notification email'); }
              }} style={{ marginTop: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                Update Email
              </button>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ display: 'block', fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Language</label>
              <select value={orgLanguage} onChange={e => setOrgLanguage(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
                <option value="de">German</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
            <input
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="colleague@company.com"
              style={{ flex: 1, minWidth: 240, padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
            />
            <select
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value)}
              style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              <option value="sdr">SDR</option>
              <option value="team_lead">Team Lead</option>
              <option value="admin">Admin</option>
            </select>
            <button onClick={handleInvite} disabled={teamLoading} style={{ padding: '12px 22px', borderRadius: 8, border: 'none', background: 'var(--accent-amber)', color: '#0F172A', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: teamLoading ? 0.6 : 1 }}>
              {teamLoading ? 'Inviting...' : <><UserPlus size={14} /> Invite</>}
            </button>
          </div>

          {teamMembers.length > 0 && (
            <div style={{ overflowX: 'auto', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <th style={teamTh}>Name</th>
                    <th style={teamTh}>Email</th>
                    <th style={teamTh}>Role</th>
                    <th style={teamTh}>Status</th>
                    <th style={teamTh}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map(m => (
                    <tr key={m.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                      <td style={teamTd}>{m.full_name || '-'}</td>
                      <td style={teamTd}>{m.email}</td>
                      <td style={teamTd}>
                        <select
                          value={m.team_role || 'sdr'}
                          onChange={e => handleChangeRole(m.id, e.target.value)}
                          disabled={m.team_role === 'owner'}
                          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', cursor: m.team_role === 'owner' ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
                        >
                          <option value="owner">Owner</option>
                          <option value="admin">Admin</option>
                          <option value="team_lead">Team Lead</option>
                          <option value="sdr">SDR</option>
                        </select>
                      </td>
                      <td style={teamTd}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: m.team_status === 'suspended' ? 'var(--error-bg)' : m.team_status === 'invited' ? 'var(--warning-bg)' : 'var(--success-bg)', color: m.team_status === 'suspended' ? 'var(--error)' : m.team_status === 'invited' ? 'var(--warning)' : 'var(--success)' }}>
                          {m.team_status || 'active'}
                        </span>
                      </td>
                      <td style={{ ...teamTd, textAlign: 'right' }}>
                        {m.team_role !== 'owner' && (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            {m.is_pending ? (
                              <button
                                onClick={() => setMemberToCancelInvite(m)}
                                title="Cancel invitation"
                                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--error)', background: 'transparent', color: 'var(--error)', cursor: 'pointer', fontSize: 12 }}
                              >
                                <XCircle size={14} />
                              </button>
                            ) : (
                              <>
                                {m.team_status !== 'suspended' && m.team_status !== 'invited' && (
                                  <button
                                    onClick={() => setMemberToSuspend(m)}
                                    title="Suspend member"
                                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--warning)', cursor: 'pointer', fontSize: 12 }}
                                  >
                                    <ShieldOff size={14} />
                                  </button>
                                )}
                                {m.team_status === 'suspended' && (
                                  <button
                                    onClick={() => setMemberToSuspend(m)}
                                    title="Activate member"
                                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--success)', background: 'transparent', color: 'var(--success)', cursor: 'pointer', fontSize: 12 }}
                                  >
                                    <CheckCircle size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => setMemberToRemove(m)}
                                  title="Remove member"
                                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--error)', background: 'transparent', color: 'var(--error)', cursor: 'pointer', fontSize: 12 }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, margin: '32px 0 12px' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Team Performance</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={perfDays} onChange={(e) => { setPerfDays(Number(e.target.value)); setTimeout(loadTeam, 100); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
                <button onClick={() => api.get(`/api/organization/team-performance/export?days=${perfDays}`, { responseType: 'blob' }).then(res => {
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url; a.download = 'team_performance.csv'; a.click();
                  window.URL.revokeObjectURL(url);
                })} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Export CSV
                </button>
              </div>
            </div>

            {performance.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>{performance.reduce((sum,p)=>sum+(p.stats?.total_leads||0),0)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Leads</div>
                </div>
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--info)' }}>{performance.reduce((sum,p)=>sum+(p.stats?.contacted||0),0)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contacted</div>
                </div>
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--warning)' }}>{performance.reduce((sum,p)=>sum+(p.stats?.demo_booked||0),0)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Demos</div>
                </div>
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--success)' }}>{performance.reduce((sum,p)=>sum+(p.stats?.closed_won||0),0)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Closed Won</div>
                </div>
              </div>
            )}
            {performance.length > 0 && (
              <div style={{ overflowX: 'auto', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                      <th style={teamTh}>Member</th>
                      <th style={teamTh}>Role</th>
                      <th style={teamTh}>Leads</th>
                      <th style={teamTh}>Contacted</th>
                      <th style={teamTh}>Replied</th>
                      <th style={teamTh}>Demos</th>
                      <th style={teamTh}>Trials</th>
                      <th style={teamTh}>Won</th>
                      <th style={teamTh}>Lost</th>
                      <th style={teamTh}>Conv.</th>
                      <th style={teamTh}>Searches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.filter(p => p.team_status !== 'invited').map(p => (
                      <tr key={p.user_id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                        <td style={teamTd}>{p.full_name || p.email}</td>
                        <td style={teamTd}>{p.team_role}</td>
                        <td style={teamTd}>{p.stats?.total_leads || 0}</td>
                        <td style={teamTd}>{p.stats?.contacted || 0}</td>
                        <td style={teamTd}>{p.stats?.replied || 0}</td>
                        <td style={teamTd}>{p.stats?.demo_booked || 0}</td>
                        <td style={teamTd}>{p.stats?.trial_started || 0}</td>
                        <td style={teamTd}>{p.stats?.closed_won || 0}</td>
                        <td style={teamTd}>{p.stats?.closed_lost || 0}</td>
                        <td style={teamTd}>{p.stats?.contacted ? Math.round(((p.stats?.closed_won || 0) / (p.stats?.contacted || 1)) * 100) + '%' : '0%'}</td>
                        <td style={teamTd}>{p.searches_used || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 32, background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px' }}>Bulk Lead Assignment</h2>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>Assign multiple leads to an SDR by entering company names, one per line.</p>
              <textarea value={bulkLeadNames} onChange={e => setBulkLeadNames(e.target.value)} rows={5} placeholder={"Company A\nCompany B\nCompany C"} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'inherit', resize: 'vertical', marginBottom: 12 }} />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <select value={bulkSdrId} onChange={e => setBulkSdrId(e.target.value)} style={{ flex: 1, minWidth: 180, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <option value="">Select SDR</option>
                  {teamMembers.filter(m => m.team_role === 'sdr').map(m => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
                </select>
                <button onClick={async () => {
                  if (!bulkSdrId || !bulkLeadNames.trim()) { toast.error('Select SDR and enter lead names'); return; }
                  try {
                    const names = bulkLeadNames.split('\n').map(s => s.trim()).filter(Boolean);
                    await api.patch('/api/leads/reassign-bulk', { company_names: names, new_sdr_owner_id: parseInt(bulkSdrId, 10) });
                    toast.success('Leads reassigned');
                    setBulkLeadNames(''); setBulkSdrId('');
                  } catch (e) { toast.error(e.response?.data?.detail || 'Bulk assignment failed'); }
                }} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: 'var(--accent-gradient)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  Reassign Leads
                </button>
              </div>
            </div>

            {teamOrg && (
              <div style={{ marginTop: 32 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, margin: '0 0 12px' }}>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Team Activity</h2>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input value={auditAdmin} onChange={e => { setAuditAdmin(e.target.value); setTimeout(() => loadTeam(1, undefined, e.target.value), 300); }} placeholder="Admin email" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 11, outline: 'none', fontFamily: 'inherit' }} />
                    <select value={auditAction} onChange={e => { setAuditAction(e.target.value); setTimeout(() => loadTeam(1, e.target.value, undefined), 300); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 11, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <option value="">All Actions</option>
                      <option value="organization_member_invited">Invited</option>
                      <option value="organization_member_removed">Removed</option>
                      <option value="organization_member_role_changed">Role Changed</option>
                      <option value="lead_reassigned">Lead Reassigned</option>
                    </select>
                    <button onClick={() => api.get('/api/organization/audit-logs/export', { responseType: 'blob' }).then(res => {
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url; a.download = 'team_activity.csv'; a.click();
                      window.URL.revokeObjectURL(url);
                    })} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                      Export CSV
                    </button>
                  </div>
                </div>
                <div style={{ overflowX: 'auto', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                        <th style={teamTh}>Time</th>
                        <th style={teamTh}>Action</th>
                        <th style={teamTh}>Admin</th>
                        <th style={teamTh}>Target</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? (
                        <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>No activity yet</td></tr>
                      ) : auditLogs.map((log, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                          <td style={teamTd}>{log.created_at ? new Date(log.created_at + 'Z').toLocaleString() : '-'}</td>
                          <td style={teamTd}>{log.action}</td>
                          <td style={teamTd}>{log.admin_email || '-'}</td>
                          <td style={teamTd}>{log.target_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
                  <button disabled={auditPage === 1} onClick={() => { const np = auditPage - 1; setAuditPage(np); setTimeout(() => loadTeam(np, auditAction, auditAdmin), 100); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>Prev</button>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{auditTotal} records</span>
                  <button disabled={auditLogs.length < 20} onClick={() => { const np = auditPage + 1; setAuditPage(np); setTimeout(() => loadTeam(np, auditAction, auditAdmin), 100); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>Next</button>
                </div>
              </div>
            )}
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 48, background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16 }}>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>You are not part of an organization yet.</p>
          <button onClick={handleCreateOrg} style={{ padding: '12px 24px', borderRadius: 8, border: 'none', background: 'var(--accent-gradient)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Create Organization
          </button>
        </div>
      )}

      {memberToSuspend && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setMemberToSuspend(null)}>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{memberToSuspend.team_status === 'suspended' ? 'Activate Member' : 'Suspend Member'}</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>
              {memberToSuspend.team_status === 'suspended'
                ? `Activate ${memberToSuspend.full_name || memberToSuspend.email}? They will regain team access.`
                : `Suspend ${memberToSuspend.full_name || memberToSuspend.email}? They will lose team access until reactivated.`}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setMemberToSuspend(null)} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={confirmSuspendMember} style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: memberToSuspend.team_status === 'suspended' ? 'var(--success)' : 'var(--warning)', color: memberToSuspend.team_status === 'suspended' ? '#fff' : '#0F172A', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                {memberToSuspend.team_status === 'suspended' ? 'Activate' : 'Suspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {memberToCancelInvite && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setMemberToCancelInvite(null)}>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>Cancel Invitation</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>
              Cancel the invitation sent to <strong>{memberToCancelInvite.email}</strong>? They will no longer be able to accept it.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setMemberToCancelInvite(null)} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Keep Invite
              </button>
              <button onClick={confirmCancelInvite} style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: 'var(--error)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                Cancel Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {memberToRemove && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setMemberToRemove(null)}>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>Remove Team Member</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>
              Remove <strong>{memberToRemove.full_name || memberToRemove.email}</strong> from the organization? They will lose team access and be moved to a free account.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setMemberToRemove(null)} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={confirmRemoveMember} style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: 'var(--error)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                Remove Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
